/**
 * Next Story Bags — PDP concierge controller.
 *
 *  • [data-modal-trigger="<name>"]  → opens <dialog data-nsb-modal="<name>"> for its section
 *    (falls back to the product page with #nsb-open=<name> when no dialog is on the page,
 *    e.g. a Featured Product section on the home page)
 *  • Concierge contact forms submit via fetch → inline success state, with a native
 *    POST fallback if Shopify serves a spam challenge
 *  • Variant changes keep the hero image, BUY NOW price label, sticky bar and
 *    hidden form fields in sync
 *  • Mobile sticky action bar appears once the in-page action block scrolls away
 */
(() => {
  if (window.NsbConcierge) return;

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`nsb:${name}`, { detail }));
    try {
      window.Shopify?.analytics?.publish?.(`nsb_${name.replace(/[^a-z0-9]+/gi, '_')}`, detail);
    } catch (e) {
      /* analytics is best-effort */
    }
  };

  /* ───────────────────────── Dialogs ───────────────────────── */
  const findModal = (name, sectionId) =>
    (sectionId && qs(`dialog[data-nsb-modal="${name}"][data-section="${sectionId}"]`)) ||
    qs(`dialog[data-nsb-modal="${name}"]`);

  const openModal = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;

    // fresh form view each time unless we're showing a just-completed success
    if (!dialog.dataset.keepSuccess) showView(dialog, 'form');
    delete dialog.dataset.keepSuccess;

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    document.documentElement.classList.add('nsb-scroll-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-visible')));

    // Focus first empty field on desktop; on touch, focus the dialog itself (no keyboard pop)
    const fine = window.matchMedia('(hover: hover)').matches;
    const target = fine ? qsa('input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea', dialog).find((el) => !el.value) : null;
    (target || qs('.nsb-modal__close', dialog))?.focus({ preventScroll: true });

    track('concierge_open', { modal: dialog.dataset.nsbModal, product: productMeta(dialog) });
  };

  const closeModal = (dialog) => {
    if (!dialog || !dialog.open || dialog.dataset.closing) return;
    dialog.dataset.closing = 'true';
    dialog.classList.remove('is-visible');
    const done = () => {
      delete dialog.dataset.closing;
      if (dialog.open) typeof dialog.close === 'function' ? dialog.close() : dialog.removeAttribute('open');
      if (!qs('dialog.nsb-modal[open]')) document.documentElement.classList.remove('nsb-scroll-lock');
      dialog._opener?.focus?.({ preventScroll: true });
    };
    reducedMotion() ? done() : setTimeout(done, 320);
  };

  const showView = (dialog, view) => {
    qsa('[data-nsb-view]', dialog).forEach((el) => (el.hidden = el.dataset.nsbView !== view));
    qs('.nsb-modal__surface', dialog)?.scrollTo?.(0, 0);
  };

  const productMeta = (dialog) => qs('[name="contact[Product]"]', dialog)?.value || '';

  const bindDialog = (dialog) => {
    if (dialog.dataset.nsbBound) return;
    dialog.dataset.nsbBound = 'true';

    // Esc → animate out instead of the instant native close
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeModal(dialog);
    });

    dialog.addEventListener('click', (e) => {
      if (e.target.closest('[data-nsb-modal-close]')) return closeModal(dialog);
      // The surface fills the dialog box, so a click whose target is the <dialog>
      // itself can only have landed on the ::backdrop.
      if (e.target === dialog) closeModal(dialog);
    });

    const tz = qs('[data-nsb-field="tz"]', dialog);
    if (tz) {
      try {
        tz.value = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      } catch (e) {
        /* ignore */
      }
    }

    const date = qs('[data-nsb-date]', dialog);
    if (date) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      const iso = (x) => new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      date.min = iso(new Date());
      if (!date.value) date.value = iso(d);
      const max = new Date();
      max.setMonth(max.getMonth() + 3);
      date.max = iso(max);
    }

    const form = qs('form[data-nsb-concierge-form]', dialog);
    if (form) bindForm(form, dialog);
  };

  /* ───────────────────────── Forms ───────────────────────── */
  const validate = (form) => {
    let firstInvalid = null;
    qsa('input, select, textarea', form).forEach((field) => {
      if (field.type === 'hidden') return;
      const ok = field.checkValidity();
      ok ? field.removeAttribute('aria-invalid') : field.setAttribute('aria-invalid', 'true');
      if (!ok && !firstInvalid) firstInvalid = field;
    });
    return firstInvalid;
  };

  const setErrors = (form, html) => {
    const box = qs('[data-nsb-errors]', form);
    if (!box) return;
    box.innerHTML = html || '';
    box.hidden = !html;
  };

  const bindForm = (form, dialog) => {
    form.addEventListener('input', (e) => e.target.removeAttribute?.('aria-invalid'));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      setErrors(form, '');

      const invalid = validate(form);
      if (invalid) {
        setErrors(form, 'Please complete the highlighted fields.');
        invalid.focus();
        return;
      }

      const submit = qs('[type="submit"]', form);
      submit?.classList.add('loading');
      submit?.setAttribute('aria-disabled', 'true');
      qs('.loading__spinner', submit)?.classList.remove('hidden');

      const finish = () => {
        submit?.classList.remove('loading');
        submit?.removeAttribute('aria-disabled');
        qs('.loading__spinner', submit)?.classList.add('hidden');
      };

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          // urlencoded, exactly like a native form post
          body: new URLSearchParams(new FormData(form)),
          headers: { Accept: 'text/html', 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'same-origin',
        });

        // Shopify bot protection → let the browser handle it natively.
        if (/\/challenge/.test(response.url)) {
          sessionStorage?.setItem?.('nsb-open', dialog.dataset.nsbModal);
          HTMLFormElement.prototype.submit.call(form);
          return;
        }

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const returned = doc.getElementById(form.id);
        const errorBox = returned && qs('[data-nsb-errors]', returned);
        const errorHtml = errorBox && !errorBox.hidden ? errorBox.innerHTML.trim() : '';
        const posted = /contact_posted=true/.test(response.url) || (returned && qs('[data-nsb-posted]', returned));

        if (response.ok && !errorHtml && (posted || !returned)) {
          onSuccess(form, dialog);
        } else {
          setErrors(form, errorHtml || 'Something went wrong — please try again, or email us directly.');
        }
      } catch (err) {
        console.error('[nsb] concierge submit failed', err);
        // network hiccup → native submit keeps the lead
        HTMLFormElement.prototype.submit.call(form);
      } finally {
        finish();
      }
    });
  };

  const onSuccess = (form, dialog) => {
    const name = (qs('[name="contact[name]"]', form)?.value || '').trim().split(/\s+/)[0];
    qsa('[data-nsb-success-name]', dialog).forEach((el) => (el.textContent = name ? `, ${name}` : ''));
    showView(dialog, 'success');
    qs('[data-nsb-view="success"] .button', dialog)?.focus({ preventScroll: true });
    track('concierge_submit', { modal: dialog.dataset.nsbModal, product: productMeta(dialog) });

    // reset the free-text bits, keep contact details for a possible second request
    qsa('textarea', form).forEach((t) => (t.value = ''));
  };

  /* ───────────────────────── Triggers ───────────────────────── */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(
      '[data-modal-trigger], a[href="#media-request-modal"], a[href="#appointment-modal"]'
    );
    if (!trigger) return;
    const name = trigger.dataset.modalTrigger || trigger.getAttribute('href').slice(1);
    const section = trigger.dataset.section || trigger.closest('[data-section]')?.dataset.section;
    const dialog = findModal(name, section);

    if (dialog) {
      event.preventDefault();
      bindDialog(dialog);
      openModal(dialog, trigger);
    } else if (trigger.dataset.productUrl) {
      window.location.href = `${trigger.dataset.productUrl}#nsb-open=${encodeURIComponent(name)}`;
    }
  });

  /* ───────────────────── Variant sync ───────────────────── */
  const onVariantChange = ({ data }) => {
    const { sectionId, html, variant } = data || {};
    if (!html) return;

    // 1. hero image
    const heroNew = html.getElementById(`NsbHero-${sectionId}`);
    qsa('[data-nsb-hero]').forEach((hero) => {
      if (heroNew && hero.id === heroNew.id && hero.innerHTML !== heroNew.innerHTML) {
        hero.classList.add('is-swapping');
        hero.innerHTML = heroNew.innerHTML;
        requestAnimationFrame(() => hero.classList.remove('is-swapping'));
      }
    });

    // 2. Buy-now label (product-form.js reads data-available-label)
    const btnNew = html.querySelector('[id^="ProductSubmitButton-"].nsb-buy-now');
    const btn = qs(`#ProductSubmitButton-${sectionId}`) || qs('.nsb-buy-now');
    if (btnNew && btn) btn.dataset.availableLabel = btnNew.dataset.availableLabel;

    // 3. sticky bar + hidden fields in dialogs
    const barNew = html.querySelector('[data-nsb-sticky-bar]');
    const bar = qs(`[data-nsb-sticky-bar][data-section="${sectionId}"]`) || qs('[data-nsb-sticky-bar]');
    if (barNew && bar) {
      const label = qs('[data-nsb-sticky-buy-label]', bar);
      const labelNew = qs('[data-nsb-sticky-buy-label]', barNew);
      if (label && labelNew) label.textContent = labelNew.textContent.trim();
      qs('[data-nsb-sticky-buy]', bar)?.toggleAttribute('disabled', !variant?.available);
    }

    qsa(`dialog.nsb-modal[data-section="${sectionId}"]`).forEach((dialog) => {
      const fresh = html.getElementById(dialog.id);
      if (!fresh) return;
      ['variant', 'variant-id', 'sku', 'price', 'url', 'availability'].forEach((key) => {
        const a = qs(`[data-nsb-field="${key}"]`, dialog);
        const b = qs(`[data-nsb-field="${key}"]`, fresh);
        if (a && b) a.value = b.value;
      });
      const meta = qs('[data-nsb-variant-meta]', dialog);
      const metaNew = qs('[data-nsb-variant-meta]', fresh);
      if (meta && metaNew) meta.innerHTML = metaNew.innerHTML;
    });
  };

  /* ───────────────────── Mobile sticky bar ───────────────────── */
  const initStickyBar = () => {
    qsa('[data-nsb-sticky-bar]').forEach((bar) => {
      if (bar.dataset.nsbBound) return;
      bar.dataset.nsbBound = 'true';

      // escape any transformed ancestor so position:fixed is truly viewport-fixed
      if (bar.parentElement !== document.body) document.body.appendChild(bar);

      const section = bar.dataset.section;
      const actions = qs(`[data-nsb-concierge][data-section="${section}"] .nsb-concierge-actions`);
      const realBuy = qs(`#ProductSubmitButton-${section}`);
      if (!actions) return;

      qs('[data-nsb-sticky-buy]', bar)?.addEventListener('click', () => {
        if (!realBuy || realBuy.disabled) return;
        realBuy.click();
      });

      // mirror disabled state from the real button (Dawn toggles it on variant change)
      if (realBuy) {
        new MutationObserver(() => {
          qs('[data-nsb-sticky-buy]', bar)?.toggleAttribute('disabled', realBuy.disabled);
        }).observe(realBuy, { attributes: true, attributeFilter: ['disabled'] });
      }

      const footer = qs('.footer') || qs('#shopify-section-footer') || qs('[id*="__footer"]');
      let actionsVisible = true;
      let footerVisible = false;
      const update = () => {
        const show = !actionsVisible && !footerVisible && !qs('dialog.nsb-modal[open]');
        bar.classList.toggle('is-visible', show);
        bar.toggleAttribute('inert', !show);
        bar.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('nsb-has-sticky-bar', show);
      };

      new IntersectionObserver(
        ([entry]) => {
          // only show once the actions have scrolled *above* the viewport
          actionsVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
          update();
        },
        { threshold: 0 }
      ).observe(actions);

      if (footer) {
        new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          update();
        }).observe(footer);
      }
    });
  };

  /* ───────────────────── Boot ───────────────────── */
  const openFromHash = () => {
    let name = null;
    const m = window.location.hash.match(/nsb-open=([\w-]+)/);
    if (m) name = decodeURIComponent(m[1]);
    else if (/^#(media-request-modal|appointment-modal)$/.test(window.location.hash)) name = window.location.hash.slice(1);

    // native-POST fallback: Shopify reloads with contact_posted=true
    const posted = qs('dialog.nsb-modal [data-nsb-posted]');
    if (posted) {
      const dialog = posted.closest('dialog');
      const form = posted.closest('form');
      if (!name || dialog.dataset.nsbModal === name || window.location.hash.includes(form.id)) {
        bindDialog(dialog);
        dialog.dataset.keepSuccess = 'true';
        showView(dialog, 'success');
        openModal(dialog);
        return;
      }
    }

    // a form with server-side errors → reopen it so the customer can fix them
    const erroredForm = qsa('dialog.nsb-modal form').find((f) => {
      const box = qs('[data-nsb-errors]', f);
      return box && !box.hidden && box.textContent.trim();
    });
    if (erroredForm) {
      const dialog = erroredForm.closest('dialog');
      bindDialog(dialog);
      openModal(dialog);
      return;
    }

    let stored = null;
    try {
      stored = sessionStorage.getItem('nsb-open');
      sessionStorage.removeItem('nsb-open');
    } catch (e) {
      /* private mode */
    }
    name = name || stored;
    if (!name) return;
    const dialog = findModal(name);
    if (dialog) {
      bindDialog(dialog);
      openModal(dialog);
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  const boot = () => {
    // showModal() renders dialogs in the browser top layer, so they never need portalling.
    qsa('dialog.nsb-modal').forEach(bindDialog);
    initStickyBar();
    openFromHash();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }
  };

  window.NsbConcierge = { open: (name, section) => openModal(findModal(name, section)), close: closeModal };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();

  // Theme editor: re-bind after section re-render
  document.addEventListener('shopify:section:load', () => {
    qsa('dialog.nsb-modal').forEach(bindDialog);
    qsa('body > [data-nsb-sticky-bar]').forEach((bar) => {
      if (qsa(`[data-nsb-sticky-bar][data-section="${bar.dataset.section}"]`).length > 1) bar.remove();
    });
    initStickyBar();
  });
})();
